import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import _ from 'underscore';
import {
  Button,
  MenuButton,
  MenuList,
  MenuDivider,
  makeStyles,
  tokens,
  Label,
  Input,
  useId,
  mergeClasses,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  shorthands,
} from '@fluentui/react-components';
import {
  Dismiss20Regular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  menuPopover: {
    minWidth: '180px',
    ...shorthands.padding('4px'),
  },
  menuButton: {
    // This is a known fluent ui issue between Popover and MenuButton: https://github.com/microsoft/fluentui/issues/31804
    // This bug has been fixed in the latest version, but as we use legacy version, update the style as a workaround.
    backgroundColor: 'transparent',
    '&[aria-expanded="true"]': {
      backgroundColor: '#EBEBEB',
    },
  },
  unselectedMenuButton: {
    '&[aria-expanded="false"]': {
      fontWeight: 'unset',
    },
  },
  enabledClearButton: {
    float: 'right',
    color: tokens.colorNeutralForeground2BrandHover,
  },
  disabledClearButton: {
    float: 'right',
    color: tokens.colorNeutralForegroundDisabled,
  },
});

const useCustomItemStyles = makeStyles({
  minWidth: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '28px',
    marginRight: '28px',
    maxWidth: '124px',
  },
  minHeight: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '28px',
    marginRight: '28px',
    marginBottom: '6px',
    maxWidth: '124px',
  },
  input: {
    paddingRight: '0px',
    height: '32px',
    lineHeight: '32px',
    overflowX: 'hidden',
    overflowY: 'hidden',
  },
  contentAfter: {
    backgroundColor: tokens.colorNeutralBackground3,
    paddingRight: '6px',
    paddingLeft: '8px',
  },
  contentLabel: {
    marginBottom: '6px',
    marginTop: '8px',
  },
});

export const ImageSizeFilter = (props) => {
  const {
    i18n,
    defaultMinWidth,
    defaultMinHeight,
    onChange,
    forceClear,
    onDismiss,
    defaultOpen = false,
  } = props;

  const minWidthId = useId('minWidth');
  const afterMinWidthId = useId('afterMinWidthId');
  const minHeightId = useId('minHeight');
  const afterMinHeightId = useId('afterMinHeightId');
  const customClasses = useCustomItemStyles();

  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [showInput, setShowInput] = useState(defaultOpen);
  const [menuList, setMenuList] = useState(null);

  // use ref to handle focus switching when pressing tab key
  const minWidthRef = useRef(null);
  const minHeightRef = useRef(null);

  const [minWidth, setMinWidth] = useState(defaultMinWidth);
  const [minHeight, setMinHeight] = useState(defaultMinHeight);

  const getOnChangeCallback = useCallback(setter => (event, newValue) => {
    const val = parseInt(newValue, 10);
    if (_.isEmpty(newValue) || _.isNaN(val)) {
      setter(null);
      return;
    }
    if (!_.isNaN(val) && val >= 0 && val <= 1000000) {
      setter(val);
    }
  }, []);

  const onMinWidthChange = useMemo(
    () => getOnChangeCallback(setMinWidth),
    [getOnChangeCallback]
  );

  const onMinHeightChange = useMemo(
    () => getOnChangeCallback(setMinHeight),
    [getOnChangeCallback]
  );

  const onClear = useCallback(() => {
    setMinWidth(null);
    setMinHeight(null);
    if ((defaultMinHeight || defaultMinWidth)) {
      // trigger onchange only when dropdown list is closed
      onChange({ newMinHeight: null, newMinWidth: null });
    }
    if (onDismiss) {
      onDismiss();
    }
  }, [defaultMinHeight, defaultMinWidth, onChange, onDismiss]);

  const title = useMemo(() => {
    if (minWidth && minHeight) {
      return `> ${minWidth}px x ${minHeight}px`;
    }
    if (minWidth) {
      return `${i18n.getString(_TL_('Width'))} > ${minWidth}px`;
    }
    if (minHeight) {
      return `${i18n.getString(_TL_('Height'))} > ${minHeight}px`;
    }
    return '';
  }, [i18n, minHeight, minWidth]);

  const focusTextField = useCallback(textFieldRef => (event) => {
    if (event.key === 'Tab') {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
      if (event.stopPropagation) { event.stopPropagation(); }
      if (event.preventDefault) { event.preventDefault(); }
    }
  }, []);

  const onMinWidthKeyUp = useMemo(() => focusTextField(minHeightRef), [focusTextField]);
  const onMinHeightKeyUp = useMemo(() => focusTextField(minWidthRef), [focusTextField]);

  const onDropdownDismiss = useCallback(() => {
    if (defaultMinHeight !== minHeight || defaultMinWidth !== minWidth) {
      onChange({ newMinWidth: minWidth, newMinHeight: minHeight }, title);
    }
  }, [defaultMinHeight, defaultMinWidth, minHeight, minWidth, onChange, title]);

  const clear = () => {
    setIsOpen(false);
    onClear();
  };

  const isSelected = useMemo(() => minWidth || minHeight, [minHeight, minWidth]);

  const onOpenChange = (e, data) => {
    setIsOpen(data.open);
  };

  useEffect(() => {
    if (forceClear) {
      clear();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceClear]);

  useEffect(() => {
    if (!isOpen) {
      onDropdownDismiss();
    }
  }, [isOpen, onDropdownDismiss]);

  useEffect(() => {
    let timeoutId;
    if (isOpen) {
      timeoutId = setTimeout(() => {
        if (minWidthRef.current) {
          minWidthRef.current.focus();
        }
      }, 300);
    }
    setShowInput(isOpen);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isOpen]);

  useEffect(() => {
    if (menuList) {
      // as we are using custom input, we need to remove menu role for accessibility
      menuList.removeAttribute('role');
    }
  }, [menuList]);

  return (
    <Popover trapFocus open={isOpen} onOpenChange={onOpenChange} positioning="below-start">
      <PopoverTrigger disableButtonEnhancement>
        <MenuButton appearance="subtle" className={isSelected ? mergeClasses(classes.menuButton, 'image-size-button') : mergeClasses(classes.menuButton, classes.unselectedMenuButton, 'image-size-button')}>
          {isSelected ? title : i18n.getString(_TL_('Image ratio'))}
        </MenuButton>
      </PopoverTrigger>

      <PopoverSurface className={mergeClasses(classes.menuPopover, 'image-size-menu')}>
        <MenuList ref={setMenuList}>
          <div className="custom-ratio">
            <div className={mergeClasses(customClasses.minWidth, 'min-width')}>
              <Label className={customClasses.contentLabel} htmlFor={minWidthId}>{i18n.getString(_TL_('Min. width'))}</Label>
              {
                // Add a condition to check if the input should be shown or not
                // By this way, screen reader can read the expanded state of Menu correctly
                showInput &&
                <Input
                  className={customClasses.input}
                  ref={minWidthRef}
                  value={minWidth ? minWidth.toString() : ''}
                  onChange={(ev, data) => onMinWidthChange(ev, data.value)}
                  contentAfter={
                    <div className={customClasses.contentAfter} id={afterMinWidthId}>
                      {i18n.getString(_TL_('px'))}
                    </div>
                }
                  aria-labelledby={`${minWidthId} ${afterMinWidthId}`}
                  id={minWidthId}
                  onKeyDown={onMinWidthKeyUp}
                />
              }
            </div>
            <div className={mergeClasses(customClasses.minHeight, 'min-height')}>
              <Label className={customClasses.contentLabel} htmlFor={minHeightId}>{i18n.getString(_TL_('Min. height'))}</Label>
              {
                showInput &&
                <Input
                  className={customClasses.input}
                  ref={minHeightRef}
                  value={minHeight ? minHeight.toString() : ''}
                  onChange={(ev, data) => onMinHeightChange(ev, data.value)}
                  contentAfter={
                    <div className={customClasses.contentAfter} id={afterMinHeightId}>
                      {i18n.getString(_TL_('px'))}
                    </div>
                  }
                  onKeyDown={onMinHeightKeyUp}
                  aria-labelledby={`${minHeightId} ${afterMinHeightId}`}
                  id={minHeightId}
                />
              }
            </div>
          </div>
          <MenuDivider />
        </MenuList>
        <div>
          <Button
            className={isSelected ? mergeClasses('clear-button', classes.enabledClearButton) : classes.disabledClearButton}
            disabled={!isSelected}
            appearance="transparent"
            icon={<Dismiss20Regular />}
            onClick={() => clear()}
          >
            {i18n.getString(_TL_('Clear'))}
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
};

